// apps/canifight/src/screens/Survey.jsx
import { useState } from 'react';
import styles from './Survey.module.css';

const QUESTIONS = [
  {
    id: 'height', section: 'SIZE & STRENGTH', sectionSub: "No judgement. We're just doing math.",
    label: 'How tall are you?',
    flavor: 'In feet and inches, or "about as tall as a standard refrigerator" — your call.',
    options: [
      { label: "Under 5'0\" (compact, low center of gravity)", value: 2 },
      { label: "5'0\" – 5'5\"", value: 4 },
      { label: "5'6\" – 5'10\" (average)", value: 5 },
      { label: "5'11\" – 6'2\"", value: 6 },
      { label: "6'3\"+ (you duck through doorways)", value: 8 },
    ],
  },
  {
    id: 'weight', section: null,
    label: "What's your weight range?",
    flavor: 'Approximately. We promise not to tell anyone.',
    options: [
      { label: 'Under 120 lbs', value: 2 },
      { label: '120–160 lbs', value: 4 },
      { label: '161–200 lbs (average)', value: 5 },
      { label: '201–250 lbs', value: 7 },
      { label: "250+ lbs (you're a unit)", value: 9 },
    ],
  },
  {
    id: 'speed', section: 'SPEED & STAMINA', sectionSub: 'How long and how fast can you go?',
    label: 'How fast do you run when you hear the microwave beep from another room?',
    flavor: 'Be honest. This is your true max speed.',
    options: [
      { label: 'I do not run. I walk purposefully.', value: 2 },
      { label: 'Light jog, protecting my back', value: 4 },
      { label: 'Solid sprint, I get there in time', value: 6 },
      { label: 'Immediate full sprint, no hesitation', value: 8 },
      { label: 'I was already there somehow', value: 10 },
    ],
  },
  {
    id: 'stamina', section: null,
    label: 'How long could you kick a soccer ball without stopping?',
    flavor: "Just kicking. No breaks. How long until you're making audible suffering noises?",
    options: [
      { label: 'Under 2 minutes', value: 2 },
      { label: '2–5 minutes', value: 4 },
      { label: '5–15 minutes', value: 6 },
      { label: '15–30 minutes', value: 8 },
      { label: "I could go all day — I'm built different", value: 10 },
    ],
  },
  {
    id: 'strength', section: 'OFFENSIVE WEAPONS', sectionSub: 'What are you working with?',
    label: 'How many grocery bags can you carry in one trip?',
    flavor: 'No second trips. Ever. A measure of raw grip and determination.',
    options: [
      { label: '1–2 bags (I have feelings too)', value: 2 },
      { label: '3–4 bags', value: 4 },
      { label: '5–6 bags (respectable)', value: 6 },
      { label: '7–8 bags (impressive)', value: 8 },
      { label: "All of them, always. It's a matter of pride.", value: 10 },
    ],
  },
  {
    id: 'nails', section: null,
    label: 'Rate the sharpness of your nails right now.',
    flavor: 'Your primary natural weapon. No shame in a good manicure.',
    options: [
      { label: 'Bitten to nothing', value: 1 },
      { label: 'Normal, harmless', value: 3 },
      { label: 'A little pointy', value: 5 },
      { label: 'Legitimately dangerous', value: 8 },
      { label: 'I could open a letter with these', value: 10 },
    ],
  },
  {
    id: 'pain', section: 'DEFENSE & TOUGHNESS', sectionSub: 'How much can you take?',
    label: 'Pain tolerance — use a stubbed toe as your baseline.',
    flavor: '1 stubbed toe = moderate suffering. Scale accordingly.',
    options: [
      { label: 'One stubbed toe ends my day entirely', value: 2 },
      { label: 'Stubbed toe = 10 minutes of recovery', value: 4 },
      { label: 'Stubbed toe, shake it off, move on', value: 6 },
      { label: "I've walked off worse than a stubbed toe", value: 8 },
      { label: 'Pain is just information I choose to ignore', value: 10 },
    ],
  },
  {
    id: 'pillow', section: 'EXPERIENCE & AGGRESSION', sectionSub: "What's your track record?",
    label: 'Pillow fight win rate?',
    flavor: 'Highly scientific measure of combat instinct.',
    options: [
      { label: "I've never been in one (suspicious)", value: 1 },
      { label: "Under 30% — I'm a lover, not a fighter", value: 3 },
      { label: 'About 50/50', value: 5 },
      { label: '70%+ — I fight to win', value: 7 },
      { label: "Undefeated. I don't talk about it.", value: 10 },
    ],
  },
  {
    id: 'aggro', section: null,
    label: 'On a scale of "I avoid conflict" to "I argue with strangers in parking lots"...',
    flavor: 'Where do you honestly land?',
    options: [
      { label: 'I apologize to furniture when I bump into it', value: 1 },
      { label: 'I avoid conflict at almost all costs', value: 3 },
      { label: "I'll stand my ground if pushed", value: 5 },
      { label: "I've been told I'm \"intense\"", value: 7 },
      { label: 'I have argued with strangers in parking lots', value: 10 },
    ],
  },
  {
    id: 'special', section: 'WILDCARDS', sectionSub: 'The intangibles that can change everything.',
    label: 'Do you have any weird physical talents?',
    flavor: 'Double jointed? Unnaturally loud scream? Hold your breath forever?',
    options: [
      { label: "Nothing unusual, I'm regular", value: 2 },
      { label: 'Mild flexibility or minor party trick', value: 4 },
      { label: 'Something genuinely weird and useful', value: 6 },
      { label: 'Legitimately supernatural tolerance or ability', value: 8 },
      { label: "I cannot explain what I can do. You wouldn't believe me.", value: 10 },
    ],
  },
  {
    id: 'iq', section: null,
    label: 'You are losing a fight. What do you do?',
    flavor: 'Your tactical intelligence in one answer.',
    options: [
      { label: 'Panic and freeze', value: 3 },
      { label: 'Fight harder, hope for the best', value: 4 },
      { label: 'Run immediately, no shame', value: 5 },
      { label: 'Fake an injury and escape', value: 7 },
      { label: 'Talk my way out somehow', value: 8 },
      { label: "I flip the script. I've never actually lost.", value: 10 },
    ],
  },
];

export default function Survey({ onComplete }) {
  const [answers, setAnswers] = useState({});

  const answered = Object.keys(answers).length;
  const pct = Math.max(8, Math.round((answered / QUESTIONS.length) * 100));

  const handleAnswer = (id, value) => {
    setAnswers(prev => ({ ...prev, [id]: value }));
  };

  const handleSubmit = () => {
    const h = answers.height || 5;
    const w = answers.weight || 5;
    const sp = answers.speed || 5;
    const st = answers.stamina || 5;
    const str = answers.strength || 5;
    const n = answers.nails || 3;
    const p = answers.pain || 5;
    const pil = answers.pillow || 5;
    const ag = answers.aggro || 5;
    const spec = answers.special || 2;
    const iq = answers.iq || 5;

    const stats = [
      Math.round((h + w) / 2),
      Math.round((str + w) / 2),
      sp,
      st,
      Math.round((str + n) / 2),
      p,
      Math.round((h + sp) / 2),
      Math.round((pil + ag) / 2),
      Math.max(1, Math.min(10, Math.round((10 - p + 3) / 2))),
      Math.round((sp + st) / 2),
      spec,
      iq,
    ].map(v => Math.max(1, Math.min(10, Math.round(v))));

    onComplete(stats);
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.title}>YOUR STATS</div>
        <div className={styles.progressWrap}>
          <div className={styles.progressLabel}>
            QUESTION {Math.min(answered + 1, QUESTIONS.length)} OF {QUESTIONS.length}
          </div>
          <div className={styles.progressBar}>
            <div className={styles.progressFill} style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>

      <div className={styles.body}>
        {QUESTIONS.map((q) => (
          <div key={q.id}>
            {q.section && (
              <>
                <div className={styles.sectionTitle}>{q.section}</div>
                {q.sectionSub && <div className={styles.sectionSub}>{q.sectionSub}</div>}
              </>
            )}
            <div className={`${styles.card} ${answers[q.id] !== undefined ? styles.cardAnswered : ''}`}>
              <div className={styles.qLabel}>{q.label}</div>
              {q.flavor && <div className={styles.qFlavor}>{q.flavor}</div>}
              <div className={styles.options}>
                {q.options.map((opt) => (
                  <button
                    key={opt.value}
                    className={`${styles.option} ${answers[q.id] === opt.value ? styles.optionSelected : ''}`}
                    onClick={() => handleAnswer(q.id, opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ))}
        <p className={styles.skipNote}>⚡ Skip any question to receive the Average Human baseline for that stat</p>
      </div>

      <div className={styles.footer}>
        <button className={styles.btnFight} onClick={handleSubmit}>
          PICK YOUR OPPONENT →
        </button>
      </div>
    </div>
  );
}
